import groupBy from 'lodash/groupBy';

import type { PenaltyOverview } from '@/warranty/domain/entities/penalty-overview';
import {
  type PenaltyOverviewChartData,
  type WarrantyPenalty,
} from '@/warranty/domain/entities/warranty-penalty-chart-data';

export function aggregatePenaltyOverviewChartToTopLevel(
  units: PenaltyOverviewChartData[],
): PenaltyOverviewChartData[] {
  const grouped = groupBy(units, (item) => item.topFullId);

  return Object.entries(grouped).map(([topFullId, items]) => {
    const { groupFullId } = items[0];
    const id = topFullId; // could be custom if needed
    const fullFrontendId = topFullId;
    const unitFullId = undefined;

    // collect all penalties across units
    const allPenalties = items.flatMap((item) => item.penalties);

    // group by penalty type
    const penaltiesByType = groupBy(allPenalties, (p) => p.type);

    // compute max for each type
    const aggregatedPenalties: WarrantyPenalty[] = Object.entries(
      penaltiesByType,
    ).map(([type, penalties]) => ({
      type: type as WarrantyPenalty['type'],
      yearToDate: Math.max(...penalties.map((p) => p.yearToDate)),
      futurePrediction: Math.max(...penalties.map((p) => p.futurePrediction)),
      windowEndDate: penalties
        .map((p) => p.windowEndDate)
        .filter(Boolean)
        .reduce(
          (latest, current) =>
            !latest || (current && current > latest) ? current : latest,
          undefined as Date | undefined,
        ),
    }));

    return {
      id,
      groupFullId,
      topFullId,
      unitFullId,
      fullFrontendId,
      penalties: aggregatedPenalties,
    };
  });
}

export function aggregatePenaltyOverviewTableToTopLevel(
  units: PenaltyOverview[],
): PenaltyOverview[] {
  const groupedByTop = groupBy(units, (item) => item.topFullId);

  const results: PenaltyOverview[] = [];

  for (const [topFullId, items] of Object.entries(groupedByTop)) {
    const { groupFullId } = items[0];
    const fullFrontendId = topFullId;
    const unitFullId = undefined;

    // Group by violation description (i.e. one row per type per top)
    const byDescription = groupBy(items, (item) => item.description);

    for (const [description, entries] of Object.entries(byDescription)) {
      results.push({
        id: `${topFullId}__${description}`,
        groupFullId,
        topFullId,
        unitFullId,
        fullFrontendId,
        description: description as PenaltyOverview['description'],
        extremeValue: Math.max(...entries.map((e) => e.extremeValue)),
        unit: entries[0].unit,
        latestDetect: new Date(
          Math.max(...entries.map((e) => e.latestDetect.getTime())),
        ),
        duration: Math.max(...entries.map((e) => e.duration)),
        totalCount: entries.reduce((sum, e) => sum + e.totalCount, 0),
        sohPenalty: Math.max(...entries.map((e) => e.sohPenalty)),
      });
    }
  }

  return results;
}
